/**
 * i18n-ready text catalog — the substitution mechanism (Req 22.11).
 *
 * Every user-facing string (labels, prompts, User_Messages) is addressed by a
 * STABLE message id rather than a hardcoded literal. A locale supplies a
 * {@link MessageCatalog}: a map from message id to that locale's text. Swapping
 * locales swaps the catalog; the resolver {@link resolveMessage} looks the id up
 * in the ACTIVE catalog and interpolates any params.
 *
 * This is deliberately structure-preserving: screens reference ids, so replacing
 * English with a regional-language catalog changes only the resolved string, not
 * the screen's structure or layout (Req 22.11). It is also the substitution
 * mechanism Property 37 (task 14.5) asserts against.
 *
 * No runtime dependency on React lives here so the catalog + resolver can be
 * unit-tested and reused outside the component tree (and stays SSR-friendly).
 */

/**
 * A stable message identifier. Ids are dotted, namespaced strings (e.g.
 * `"auth.login.title"`). The concrete union of known ids is derived from the
 * English catalog (see `messages.ts`) so the catalog is the single source of
 * truth and screens get compile-time checking of the ids they use.
 */
export type MessageId = string;

/**
 * Interpolation parameters for a message. Values are substituted into `{name}`
 * placeholders in the message text. Kept to primitive types so a message is a
 * pure, deterministic string transform (SSR-safe, no formatting side effects).
 */
export type MessageParams = Readonly<Record<string, string | number>>;

/**
 * A locale's message catalog: a total map from message id to text. Each locale
 * (en, and later regional languages) provides one. The catalog is keyed by the
 * id type so a locale catalog missing an id, or adding an unknown one, is a
 * compile-time error when the id type is a finite union.
 */
export type MessageCatalog<Id extends MessageId = MessageId> = Readonly<
  Record<Id, string>
>;

/** A BCP-47-ish locale tag (e.g. `"en"`, `"hi"`, `"ta"`). */
export type LocaleTag = string;

/**
 * Resolve a message id against a catalog, interpolating params.
 *
 * Fail-safe by design: if the id is absent from the active catalog (e.g. a
 * regional catalog has not yet translated it), the id itself is returned as a
 * clear, visible fallback rather than throwing or rendering `undefined`. This
 * keeps the UI operable and makes an untranslated string easy to spot, while
 * never breaking structure/layout (Req 22.11).
 *
 * Interpolation replaces every `{key}` placeholder with the corresponding value
 * from `params`. A placeholder with no matching param is left as-is (again,
 * visible rather than silently blank). Interpolation is purely positional on
 * the resolved template, so it behaves identically across locales.
 */
export function resolveMessage(
  catalog: MessageCatalog,
  id: MessageId,
  params?: MessageParams,
): string {
  const template = Object.prototype.hasOwnProperty.call(catalog, id)
    ? catalog[id]
    : undefined;
  if (template === undefined) {
    // Missing id: return the id as a clear, non-throwing fallback.
    return id;
  }
  if (params === undefined) {
    return template;
  }
  return interpolate(template, params);
}

/** Replace `{key}` placeholders in `template` with values from `params`. */
function interpolate(template: string, params: MessageParams): string {
  return template.replace(/\{(\w+)\}/g, (whole: string, key: string): string => {
    if (Object.prototype.hasOwnProperty.call(params, key)) {
      return String(params[key]);
    }
    // Unknown placeholder: keep it literal so it is visible, not blank.
    return whole;
  });
}

/**
 * A bound resolver: `t(id, params?)`. Given an active catalog, returns a
 * function that resolves ids against it. This is the shape components consume
 * (via `useT()`), and it can also be used directly in non-React contexts.
 */
export type TFunction = (id: MessageId, params?: MessageParams) => string;

/** Build a bound {@link TFunction} for a given catalog. */
export function makeT(catalog: MessageCatalog): TFunction {
  return (id: MessageId, params?: MessageParams): string =>
    resolveMessage(catalog, id, params);
}
